import {
  continueWatching,
  createPlaylist,
  favoriteList,
  feedCategories,
  feedProviders,
  getState,
  isCategoryVisible,
  setCategoryVisible,
  setFeedCategories,
  setFeedProviders,
  setSearchCategories,
  updateSettings,
} from "./store.ts";
import { el, showToast } from "./dom.ts";
import { icons } from "./icons.ts";
import { categoryTitle } from "./categories.ts";
import { visibleProviders } from "./visibility.ts";
import type { AppContext } from "./views.ts";

function switchControl(label: string, active: boolean, onChange: (next: boolean) => void): HTMLButtonElement {
  const toggle = el("button", {
    class: "switch",
    attrs: { type: "button", role: "switch", "aria-checked": String(active), "aria-label": label },
  });
  toggle.append(el("span", { class: "switch-knob" }));
  toggle.addEventListener("click", () => {
    const next = toggle.getAttribute("aria-checked") !== "true";
    toggle.setAttribute("aria-checked", String(next));
    onChange(next);
  });
  return toggle;
}

function row(titleText: string, description: string, control: HTMLElement): HTMLElement {
  return el(
    "div",
    { class: "settings-row" },
    el(
      "div",
      { class: "settings-row-copy" },
      el("p", { class: "settings-row-title", text: titleText }),
      el("p", { class: "settings-row-sub", text: description }),
    ),
    control,
  );
}

/**
 * Settings is a page, not a drawer: a rail of groups on the left and the rows
 * they govern on the right. Every change applies on tap.
 */
export function settingsView(context: AppContext): HTMLElement {
  const state = getState();
  const categories = [...new Set(context.providers.map((provider) => provider.category))];
  const countIn = (list: string[]): number =>
    context.providers.filter((provider) => list.includes(provider.category)).length;

  const root = el("div", { class: "view settings-view" });

  // ---- rail -----------------------------------------------------------
  const groups: { id: string; label: string; summary: string }[] = [
    { id: "settings-appearance", label: "Appearance", summary: "Density, motion and artwork" },
    { id: "settings-feed", label: "Feed", summary: "Order and shape the home page" },
    { id: "settings-search", label: "Search scope", summary: "Which categories a search reads" },
    { id: "settings-playback", label: "Playback", summary: "Autoplay, sound and quality" },
    { id: "settings-library", label: "Library", summary: "Playlists and local data" },
  ];
  const railLinks = new Map<string, HTMLButtonElement>();
  const rail = el("nav", { class: "settings-rail", attrs: { "aria-label": "Settings groups" } });
  for (const group of groups) {
    const link = el("button", {
      class: "settings-rail-link",
      attrs: { type: "button", "aria-current": group.id === groups[0].id ? "true" : "false" },
      text: group.label,
    });
    link.addEventListener("click", () => {
      const target = document.getElementById(group.id);
      if (target instanceof HTMLDetailsElement) target.open = true;
      target?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "start",
      });
      for (const [id, other] of railLinks) other.setAttribute("aria-current", String(id === group.id));
    });
    railLinks.set(group.id, link);
    rail.append(link);
  }

  // ---- feed composer --------------------------------------------------
  const composer = el("ol", { class: "composer" });

  const orderedFeed = (): string[] => {
    const chosen = feedCategories().filter((category) => categories.includes(category));
    const rest = categories.filter(
      (category) => isCategoryVisible(category) && !chosen.includes(category),
    );
    return [...chosen, ...rest];
  };

  const paintComposer = (): void => {
    const order = orderedFeed();
    composer.replaceChildren();
    order.forEach((category, index) => {
      const visible = isCategoryVisible(category);
      const move = (delta: number) => {
        const next = [...order];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        setFeedCategories(next);
        paintComposer();
      };

      composer.append(
        el(
          "li",
          { class: "composer-row", attrs: { "data-hidden": String(!visible) } },
          el("span", { class: "composer-index", text: String(index + 1).padStart(2, "0") }),
          el("span", { class: "composer-name", text: categoryTitle(category) }),
          el("span", { class: "composer-count", text: `${countIn([category])}` }),
          el(
            "span",
            { class: "composer-move" },
            el("button", {
              class: "coin coin--sm",
              attrs: { type: "button", "aria-label": `Move ${categoryTitle(category)} up` },
              html: icons.chevron,
              on: { click: () => move(-1) },
            }),
            el("button", {
              class: "coin coin--sm coin--down",
              attrs: { type: "button", "aria-label": `Move ${categoryTitle(category)} down` },
              html: icons.chevron,
              on: { click: () => move(1) },
            }),
          ),
          switchControl(`${categoryTitle(category)} rows visible`, visible, (next) => {
            setCategoryVisible(category, next);
            paintComposer();
            paintScope();
          }),
        ),
      );
    });
  };

  // ---- provider picker ------------------------------------------------
  // 221 working providers are too many to mount at once, so the list is a
  // fixed-height window: only the rows around the scroll position exist in the
  // DOM, and search narrows the same window instead of replacing it.
  const ROW_HEIGHT = 44;
  const workingProviders = visibleProviders(context.providers)
    .filter((provider) => provider.state !== "offline")
    .sort((left, right) => left.name.localeCompare(right.name));
  let matches = workingProviders;

  const pickerSearch = el("input", {
    class: "input feed-picker-search",
    attrs: { type: "search", placeholder: "Find a provider", "aria-label": "Find a provider" },
  }) as HTMLInputElement;
  const pickerCount = el("span", { class: "feed-picker-count" });
  const pickerAll = el("button", {
    class: "tag",
    attrs: { type: "button", "aria-pressed": "true" },
    text: "Every working provider",
  });
  const pickerRows = el("div", { class: "feed-picker-rows" });
  const pickerList = el(
    "div",
    { class: "feed-picker-list", attrs: { role: "list", "aria-label": "Providers on the home page" } },
    pickerRows,
  );

  const paintPickerWindow = (): void => {
    const total = matches.length;
    const viewport = pickerList.clientHeight || 308;
    const start = Math.max(0, Math.floor(pickerList.scrollTop / ROW_HEIGHT) - 2);
    const end = Math.min(total, start + Math.ceil(viewport / ROW_HEIGHT) + 4);
    const stored = feedProviders();
    // An empty list means every working provider, and every switch reads on.
    const active = new Set(stored.length > 0 ? stored : workingProviders.map((provider) => provider.id));
    const rows: HTMLElement[] = [];
    for (let index = start; index < end; index += 1) {
      const provider = matches[index];
      rows.push(
        el(
          "div",
          {
            class: "feed-picker-row",
            style: `top:${index * ROW_HEIGHT}px`,
            attrs: { role: "listitem", "aria-setsize": String(total), "aria-posinset": String(index + 1) },
          },
          el("span", { class: "feed-picker-name", text: provider.name }),
          el("span", { class: "feed-picker-cat", text: categoryTitle(provider.category) }),
          switchControl(`${provider.name} on the home page`, active.has(provider.id), (next) => {
            const current = active;
            const chosen = next
              ? [...current, provider.id]
              : [...current].filter((id) => id !== provider.id);
            setFeedProviders(chosen);
            drawPicker();
          }),
        ),
      );
    }
    pickerRows.replaceChildren(...rows);
  };

  const drawPicker = (): void => {
    const query = pickerSearch.value.trim().toLowerCase();
    matches = workingProviders.filter(
      (provider) =>
        !query ||
        provider.name.toLowerCase().includes(query) ||
        provider.id.toLowerCase().includes(query) ||
        categoryTitle(provider.category).toLowerCase().includes(query),
    );
    const chosen = feedProviders();
    pickerRows.style.height = `${matches.length * ROW_HEIGHT}px`;
    pickerCount.textContent = `${chosen.length || workingProviders.length} selected`;
    pickerAll.setAttribute("aria-pressed", String(chosen.length === 0));
    paintPickerWindow();
  };

  pickerSearch.addEventListener("input", () => {
    pickerList.scrollTop = 0;
    drawPicker();
  });
  pickerList.addEventListener("scroll", paintPickerWindow, { passive: true });
  pickerAll.addEventListener("click", () => {
    setFeedProviders([]);
    drawPicker();
  });

  const providerPicker = el(
    "div",
    { class: "feed-picker" },
    el(
      "div",
      { class: "feed-picker-head" },
      el("p", { class: "settings-row-title", text: "Providers" }),
      pickerCount,
      pickerAll,
    ),
    el("p", {
      class: "settings-row-sub",
      text: "Each row on the home page comes from one of these sites. No choice means every working provider.",
    }),
    pickerSearch,
    pickerList,
  );

  // ---- search scope ---------------------------------------------------
  const scopeChips = el("div", { class: "scope-row" });
  const scopeLine = el("p", { class: "settings-row-sub" });
  const scopeState = (): string[] => {
    const chosen = state.settings.searchCategories;
    return chosen.length > 0 ? chosen.filter(isCategoryVisible) : categories.filter(isCategoryVisible);
  };
  const paintScope = (): void => {
    const active = new Set(scopeState());
    const chips: HTMLElement[] = [
      el("button", {
        class: "tag",
        attrs: {
          type: "button",
          "aria-pressed": String(scopeState().length === categories.filter(isCategoryVisible).length),
        },
        text: "Everything on",
        on: {
          click: () => {
            setSearchCategories([]);
            paintScope();
          },
        },
      }),
    ];
    for (const category of categories.filter(isCategoryVisible)) {
      chips.push(
        el("button", {
          class: "tag",
          attrs: { type: "button", "aria-pressed": String(active.has(category)) },
          text: categoryTitle(category),
          on: {
            click: () => {
              const next = new Set(scopeState());
              if (next.has(category)) next.delete(category);
              else next.add(category);
              setSearchCategories([...next]);
              paintScope();
            },
          },
        }),
      );
    }
    scopeChips.replaceChildren(...chips);
    scopeLine.textContent = `Search reads ${countIn(scopeState())} of ${context.providers.length} providers.`;
  };

  // ---- appearance -----------------------------------------------------
  const accentRow = el("div", { class: "tag-row", attrs: { role: "group", "aria-label": "Accent colour" } });
  for (const [value, label] of [
    ["a", "Periwinkle"],
    ["b", "Coral"],
    ["c", "Gold"],
  ] as const) {
    const button = el("button", {
      class: "tag",
      attrs: { type: "button", "aria-pressed": String(state.settings.skin === value) },
      text: label,
    });
    button.addEventListener("click", () => {
      updateSettings({ skin: value, skinPicked: true });
      for (const other of accentRow.children) other.setAttribute("aria-pressed", String(other === button));
    });
    accentRow.append(button);
  }

  const densityRow = el("div", { class: "tag-row", attrs: { role: "group", "aria-label": "Poster density" } });
  const densityButtons = new Map<string, HTMLButtonElement>();
  for (const [value, label] of [
    ["compact", "Compact"],
    ["comfortable", "Comfortable"],
    ["roomy", "Roomy"],
  ]) {
    const button = el("button", {
      class: "tag",
      attrs: { type: "button", "aria-pressed": String(state.settings.density === value) },
      text: label,
    });
    button.addEventListener("click", () => {
      updateSettings({ density: value as "compact" | "comfortable" | "roomy" });
      for (const [key, other] of densityButtons) {
        other.setAttribute("aria-pressed", String(key === value));
      }
    });
    densityButtons.set(value, button);
    densityRow.append(button);
  }

  const motionSelect = el("select", {
    class: "select",
    attrs: { "aria-label": "Motion", name: "motion" },
  }) as HTMLSelectElement;
  for (const [value, label] of [
    ["system", "Follow the system"],
    ["always", "Always reduce"],
    ["never", "Never reduce"],
  ]) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = label;
    motionSelect.append(option);
  }
  motionSelect.value = state.settings.reduceMotion;
  motionSelect.addEventListener("change", () => {
    updateSettings({ reduceMotion: motionSelect.value as "system" | "always" | "never" });
  });

  // ---- playback -------------------------------------------------------
  const quality = el("select", {
    class: "select",
    attrs: { "aria-label": "Default quality", name: "quality" },
  }) as HTMLSelectElement;
  for (const [value, label] of [
    ["auto", "Automatic"],
    ["1080", "1080p"],
    ["720", "720p"],
    ["480", "480p, starts faster"],
  ]) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = label;
    quality.append(option);
  }
  quality.value = state.settings.quality;
  quality.addEventListener("change", () => {
    updateSettings({ quality: quality.value as "auto" | "1080" | "720" | "480" });
  });

  // ---- library data ---------------------------------------------------
  const libraryLine = el("p", {
    class: "settings-row-sub",
    text: `${favoriteList().length} favourites, ${continueWatching().length} in progress, ${state.playlists.length} playlists`,
  });
  const clear = el("button", {
    class: "btn btn-secondary btn-sm",
    attrs: { type: "button" },
    text: "Clear watch history and favourites",
  });
  let armed = false;
  clear.addEventListener("click", () => {
    if (!armed) {
      armed = true;
      clear.textContent = "Click again to erase everything";
      clear.classList.add("btn-danger");
      window.setTimeout(() => {
        armed = false;
        clear.textContent = "Clear watch history and favourites";
        clear.classList.remove("btn-danger");
      }, 6000);
      return;
    }
    localStorage.removeItem("aiox.state.v1");
    showToast("Local library cleared");
    location.reload();
  });

  paintComposer();
  paintScope();

  const group = (
    id: string,
    label: string,
    children: HTMLElement[],
    open = false,
  ): HTMLElement => {
    const node = el("details", {
      class: "settings-group",
      attrs: { id, ...(open ? { open: "true" } : {}) },
    });
    node.append(
      el(
        "summary",
        { class: "settings-group-summary" },
        el("span", {
          class: "settings-group-title",
          attrs: { role: "heading", "aria-level": "2" },
          text: label,
        }),
        el("span", { class: "settings-group-chevron", html: icons.chevronDown }),
      ),
      el("div", { class: "settings-group-body" }, ...children),
    );
    return node;
  };

  const pane = el(
    "div",
    { class: "settings-pane" },
    group(
      "settings-appearance",
      "Appearance",
      [
        row("Accent", "The wall stays dark; the accent is yours.", accentRow),
        row("Poster density", "One control sets the poster size in every grid and row.", densityRow),
        row("Motion", "Reduced motion removes movement and keeps the state change.", motionSelect),
        row(
          "Blur artwork",
          "Hide previews until you ask to see them, card by card.",
          switchControl("Blur artwork", state.settings.blurArtwork, (next) => updateSettings({ blurArtwork: next })),
        ),
      ],
      true,
    ),
    group("settings-feed", "Feed", [
      el("p", {
        class: "settings-row-sub",
        text: "Choose the sites and category rows the home page paints. Hidden categories keep their place so the order survives.",
      }),
      composer,
      providerPicker,
    ]),
    group("settings-search", "Search scope", [scopeLine, scopeChips]),
    group("settings-playback", "Playback", [
      row(
        "Autoplay next title",
        "Continue with the next queued title when a video ends.",
        switchControl("Autoplay next title", state.settings.autoplay, (next) => updateSettings({ autoplay: next })),
      ),
      row(
        "Start muted",
        "Videos begin without sound.",
        switchControl("Start muted", state.settings.muted, (next) => updateSettings({ muted: next })),
      ),
      row(
        "Resume where you stopped",
        "Reopen a title at its last position.",
        switchControl("Resume where you stopped", state.settings.resume, (next) => updateSettings({ resume: next })),
      ),
      row("Default quality", "Used when a provider exposes several streams.", quality),
    ]),
    group("settings-library", "Library", [
      libraryLine,
      el(
        "div",
        { class: "settings-actions" },
        el("button", {
          class: "btn btn-secondary btn-sm",
          attrs: { type: "button" },
          text: "New playlist",
          on: {
            click: () => {
              const playlistName = `Playlist ${state.playlists.length + 1}`;
              createPlaylist(playlistName);
              showToast(`Created ${playlistName}`);
              context.navigate("#/library");
            },
          },
        }),
        clear,
      ),
    ]),
  );

  root.append(
    el(
      "header",
      { class: "page-head container" },
      el(
        "div",
        { class: "page-head-copy" },
        el("h1", { class: "page-title", text: "Settings" }),
        el("p", { class: "page-sub", text: "Changes apply on tap. There is no save button." }),
      ),
    ),
    el("div", { class: "container settings-layout" }, rail, pane),
  );

  // The picker window needs a laid-out height before it can decide how many
  // rows to mount.
  drawPicker();

  return root;
}
